import { Role, User, useUserRoleSelectUsersRolesQuery } from 'schema'
import { SEPARATOR } from '../types'
import { useOptions } from '../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useUserSelectLabel } from '../UserSelect/hooks/useUserSelectLabel'
import { useUserSelectTooltip } from '../UserSelect/hooks/useUserSelectTooltip'
import { useUserDeleted } from 'hooks/useUserDeleted'
import { createStrictContext } from 'utils/createContext'
import {
  ToLabeledValuesOptions,
  toLabeledValues,
} from '../../utils/labeledValues'
import { UserRoleSelectContext, UserSelectRole } from './types'
import { UserSelectUser } from '../UserSelect/types'
import { alphabeticalCompare } from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } = createStrictContext<UserRoleSelectContext>(
  'UserRoleSelectContext'
)

/**
 * returns the context associated with the {@link UserRoleSelectProvider}
 *
 * @throws if the UserRoleSelectProvider is not found
 */
export const useUserRoleSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the UserRoleSelect
 *
 * @param props
 */
const UserRoleSelectProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const { data, loading } = useUserRoleSelectUsersRolesQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const userLabel = useUserSelectLabel()
  const userTooltip = useUserSelectTooltip()
  const userDeleted = useUserDeleted()
  const toValue = ({
    __typename,
    id,
  }: Pick<User | Role, '__typename' | 'id'>) => `${__typename}${SEPARATOR}${id}`
  const users = useOptions({
    items: data?.project?.users,
    value: toValue,
    label: userLabel,
    tooltip: userTooltip,
    sort: (a, b) => alphabeticalCompare(userLabel(a), userLabel(b)),
  })
  const roles = useOptions({
    items: data?.project?.roles,
    value: toValue,
    label: 'name',
    tooltip: ({ users }) => `${users.length} users`,
    sort: 'name',
  })
  const toLabeledValuesOptions: ToLabeledValuesOptions<
    UserSelectUser | UserSelectRole
  > = {
    value: toValue,
    label: (ur) =>
      ur.__typename === 'Role'
        ? ur.name
        : ur.__typename === 'User'
        ? userLabel(ur)
        : 'Invalid __typename',
    tooltip: (ur) =>
      ur.__typename === 'User'
        ? userTooltip(ur)
        : ur.__typename === 'Role'
        ? `${ur.users.length} users`
        : undefined,
    deleted: (ur) => (ur.__typename === 'User' ? userDeleted(ur) : undefined),
  }
  return (
    <Provider
      value={{
        loading,
        options: [
          { label: 'Roles', value: 1, options: roles },
          { label: 'Users', value: 2, options: users },
        ],
        fromLabeledValues: (values) =>
          (values ?? []).reduce(
            (prev, { value }) => {
              const [type, id] = (value as string).split(SEPARATOR) as [
                User['__typename'] | Role['__typename'],
                string
              ]
              if (type === 'User') prev.userIds.push(id)
              else if (type === 'Role') prev.roleIds.push(id)
              return prev
            },
            { userIds: Array<string>(), roleIds: Array<string>() }
          ),
        fromLabeledValue: (value) => {
          if (!value) return undefined
          const [type, id] = (value.value as string).split(SEPARATOR) as [
            User['__typename'] | Role['__typename'],
            string
          ]
          if (type === 'User') return { userId: id }
          else if (type === 'Role') return { roleId: id }
          return undefined
        },
        toLabeledValues: (usersRoles) =>
          toLabeledValues(
            [...(usersRoles?.roles ?? []), ...(usersRoles?.users ?? [])],
            toLabeledValuesOptions
          ),
        toLabeledValue: (userRole) =>
          userRole
            ? toLabeledValues([userRole], toLabeledValuesOptions)[0]
            : undefined,
      }}
    >
      {children}
    </Provider>
  )
}

export default UserRoleSelectProvider
